import { List } from 'react-virtualized';
import { MAX_HEIGHT, OPTION_HEIGHT, OPTION_GROUP_HEIGHT, OPTION_WITH_HELP_HEIGHT, OPTION_LOADING_HEIGHT } from '../../constants';

// Constants for text measurement calculations
import { jsx as _jsx } from "react/jsx-runtime";
const HORIZONTAL_PADDING = 32; // 16px padding on each side
const AVERAGE_CHAR_WIDTH = 7; // Average character width for detail-200 typography - required for both themes
const DEFAULT_CHARS_PER_LINE = 50; // Default characters per line when menu width is not available
const HELP_TEXT_LINE_HEIGHT = 18; // Line height for help text
const CACHE_TOLERANCE = 5; // Tolerance for cache updates (in pixels)
const HELP_TEXT_PADDING = 6; // Padding for help text

function isGroup(option) {
  return option && Array.isArray(option.options);
}

// Cache for help text height measurements - only used when help text is present
const helpTextHeightCache = new Map();
export const clearHelpTextHeightCache = () => {
  helpTextHeightCache.clear();
};
function measureStyledTextHeight(helpText, menuWidth) {
  // Early return for empty help text
  if (!helpText || helpText.trim() === '') {
    return OPTION_WITH_HELP_HEIGHT;
  }

  // Create cache key based on help text content and menu width
  const cacheKey = `${helpText}-${menuWidth}`;
  if (helpTextHeightCache.has(cacheKey)) {
    return helpTextHeightCache.get(cacheKey);
  }

  // Calculate how many characters can fit per line based on menu width
  const charsPerLine = menuWidth ? Math.floor((menuWidth - HORIZONTAL_PADDING) / AVERAGE_CHAR_WIDTH) : DEFAULT_CHARS_PER_LINE;

  // Estimate how many lines the help text will take up
  const estimatedLines = Math.ceil(helpText.length / charsPerLine);

  // Calculate total option height based on estimated lines:
  // Use base OPTION_HEIGHT (40px) + help text lines × line height, with fallback to OPTION_WITH_HELP_HEIGHT (64px)
  const totalHeight = Math.max(OPTION_HEIGHT + HELP_TEXT_PADDING + estimatedLines * HELP_TEXT_LINE_HEIGHT, OPTION_WITH_HELP_HEIGHT);
  helpTextHeightCache.set(cacheKey, totalHeight);
  return totalHeight;
}

// Only update cache with actual measurements when help text is present
function updateCacheWithActualMeasurements(options, menuWidth) {
  // Only run measurement logic if there are options with help text
  const optionsWithHelp = options.filter(option => option.help);
  if (optionsWithHelp.length === 0) {
    return;
  }
  const helpTextElements = document.querySelectorAll('small');
  for (const element of helpTextElements) {
    var _element$textContent;
    const elementText = (_element$textContent = element.textContent) === null || _element$textContent === void 0 ? void 0 : _element$textContent.trim();
    if (!elementText) continue;
    const matchingOption = options.find(option => {
      var _option$help;
      return ((_option$help = option.help) === null || _option$help === void 0 ? void 0 : _option$help.toString()) === elementText;
    });
    if (!matchingOption) continue;
    const rect = element.getBoundingClientRect();
    const actualHeight = rect.height;
    const totalHeight = OPTION_HEIGHT + actualHeight;
    const cacheKey = `${elementText}-${menuWidth}`;
    const cachedHeight = helpTextHeightCache.get(cacheKey);
    if (!cachedHeight || Math.abs(cachedHeight - totalHeight) > CACHE_TOLERANCE) {
      helpTextHeightCache.set(cacheKey, totalHeight);
    }
  }
}
function getOptionHeight(option, menuWidth) {
  if (isGroup(option)) {
    return OPTION_GROUP_HEIGHT + option.options.reduce((total, child) => {
      return total + getOptionHeight(child, menuWidth);
    }, 0);
  }
  if (option.help) {
    // Only use measurement logic when help text is present
    return measureStyledTextHeight(option.help.toString(), menuWidth);
  }
  if (option.loading) {
    return OPTION_LOADING_HEIGHT;
  }
  return OPTION_HEIGHT;
}
function calculateListHeight(options, menuWidth) {
  let height = 0;
  for (let optionIndex = 0; optionIndex < options.length; optionIndex++) {
    const option = options[optionIndex];
    height += getOptionHeight(option, menuWidth);
    if (height > MAX_HEIGHT) {
      return MAX_HEIGHT;
    }
  }
  return height;
}
const menuRendererCache = {};
export const virtualizedMenuRenderer = (menuWidth, virtualizedMenuRendererRefCallback) => {
  const cacheKey = `menuWidth-${menuWidth}`;
  if (!menuRendererCache[cacheKey]) {
    clearHelpTextHeightCache();
  }
  if (!menuRendererCache[cacheKey]) {
    menuRendererCache[cacheKey] = ({
      focusedOption,
      instancePrefix,
      onFocus,
      onOptionRef,
      onSelect,
      optionComponent,
      optionGroupComponent,
      optionRenderer,
      options,
      valueArray,
      valueKey,
      optionClassName
    }) => {
      const focusedOptionIndex = options.indexOf(focusedOption);
      const height = calculateListHeight(options, menuWidth);
      const OptionComponent = optionComponent;
      const OptionGroupComponent = optionGroupComponent;
      const renderLabel = optionRenderer;

      // The styles are generated by react-virtualized to control positioning
      function renderOption(option, i, style) {
        if (isGroup(option)) {
          return /*#__PURE__*/_jsx("span", {
            style: style,
            children: /*#__PURE__*/_jsx(OptionGroupComponent, {
              className: "Select-option-group",
              label: renderLabel(option),
              option: option,
              optionIndex: i,
              children: option.options.map((opt, idx) => renderOption(opt, idx))
            })
          }, `option-group-${i}`);
        }
        const isSelected = valueArray && valueArray.indexOf(option) > -1;
        const isFocused = option === focusedOption;
        // These class names ('Select-option', 'is-selected', 'is-focused', 'is-disabled') are load-bearing.
        // They are historically provided by react-select-plus and are relied upon by both our own styles
        // and by consuming apps for custom styling and selectors. Removing or renaming these would break
        // downstream styling and possibly built-in react-select-plus behaviors. If you change this, ensure
        // all dependent styles and selectors are updated, and add/maintain tests for their presence.
        const optionClass = [optionClassName, 'Select-option', isSelected && 'is-selected', isFocused && 'is-focused', option.disabled && 'is-disabled'].filter(Boolean).join(' ');
        return /*#__PURE__*/_jsx("span", {
          style: style,
          children: /*#__PURE__*/_jsx(OptionComponent, {
            className: optionClass,
            instancePrefix: instancePrefix,
            isDisabled: option.disabled,
            isFocused: isFocused,
            isSelected: isSelected,
            onFocus: onFocus,
            onSelect: onSelect,
            option: option,
            optionIndex: i,
            ref: ref => {
              onOptionRef(ref, isFocused);
            },
            children: renderLabel(option, i)
          })
        }, `option-${i}-${option[valueKey]}`);
      }
      function getRowHeight({
        index
      }) {
        return getOptionHeight(options[index], menuWidth);
      }
      function renderRow({
        index,
        style
      }) {
        const option = options[index];
        return renderOption(option, index, style);
      }
      return /*#__PURE__*/_jsx(List, {
        height: height,
        rowCount: options.length,
        rowHeight: getRowHeight,
        rowRenderer: renderRow,
        scrollToIndex: focusedOptionIndex,
        width: menuWidth,
        ref: virtualizedMenuRendererRefCallback,
        onScroll: () => {
          // Only run measurement update if there are options with help text
          const hasHelpText = options.some(option => option.help);
          if (hasHelpText) {
            setTimeout(() => {
              updateCacheWithActualMeasurements(options, menuWidth);
            }, 100);
          }
        }
      });
    };
  }
  menuRendererCache[cacheKey].displayName = 'virtualizedMenuRenderer'; // this is required for isVirtualized (https://git.hubteam.com/HubSpot/foundations-components/blob/92a96eba1587b9035f210c72b069b05f6f685b3d/foundations-components/static-1.7715/js/transitional/components/Select/Select.helpers.tsx#L116) to work
  return menuRendererCache[cacheKey];
};
virtualizedMenuRenderer.displayName = 'virtualizedMenuRenderer';